/**
 * The quiet top-right corner.
 *
 * These chips exist to answer one question — "is it still working?" — and nothing else.
 * The offline chip says *"companion offline — still listening"* rather than "network
 * error" because the second half of that sentence is the important half: recognition is
 * local, the graph is local, the history is local, and the only thing that stopped is the
 * part that suggests where to go. A red banner here would misdescribe the situation.
 *
 * On the Go the same chip has to end differently, because "still listening" would be a
 * claim about a microphone that was never opened — and every mic-implying chip is dropped
 * for the same reason. `state.listening` is not the discriminator: the reducer promotes it
 * to `'active'` on the first appended chord whether that chord was played or tapped, so
 * these key off `mode` and only off `mode`.
 */

import { AnimatePresence, motion } from 'framer-motion';
import type { SessionState } from '@hc/shared';
import { CALM_FADE } from '../graph/motion.js';
import type { SessionMode } from './sessionView.js';

export interface StatusChipsProps {
  state: SessionState;
  /** Passed in rather than derived so it agrees with the rest of the screen. */
  companionOffline: boolean;
  /** Absent means `'piano'`. */
  mode?: SessionMode;
  /**
   * True when nothing can be written to this device. The one thing an unsupported browser
   * still costs someone On the Go, so it is the one thing still worth saying.
   */
  storageUnavailable?: boolean;
  /**
   * Offered when a piano-mode session exists but the microphone was never opened — the
   * shape every restored or continued session arrives in. Tapping is the user gesture
   * that iOS requires, and the host preserves the path (see `startListening`).
   */
  onStartListening?: (() => void) | undefined;
}

export function StatusChips({
  state,
  companionOffline,
  mode = 'piano',
  storageUnavailable = false,
  onStartListening,
}: StatusChipsProps): React.JSX.Element {
  const onTheGo = mode === 'on-the-go';
  const paused = !onTheGo && state.listening === 'paused';
  const notListening =
    !onTheGo &&
    state.micStatus === 'unrequested' &&
    state.path.length > 0 &&
    Boolean(onStartListening);
  const usingFallback = Object.values(state.branchSets).some((set) => set.status === 'fallback');

  return (
    <div className="hc-chips" data-testid="hc-status-chips">
      <AnimatePresence initial={false}>
        {notListening ? (
          <motion.button
            key="not-listening"
            type="button"
            className="hc-chip hc-chip--action"
            data-testid="hc-chip-start-listening"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={CALM_FADE}
            onClick={onStartListening}
          >
            not listening — tap to start
          </motion.button>
        ) : null}

        {onTheGo ? (
          <motion.span
            key="mode"
            className="hc-chip hc-chip--mode"
            data-testid="hc-chip-mode"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={CALM_FADE}
          >
            on the go
          </motion.span>
        ) : null}

        {companionOffline ? (
          <motion.span
            key="offline"
            className="hc-chip hc-chip--offline"
            data-testid="hc-chip-offline"
            role="status"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={CALM_FADE}
          >
            {onTheGo ? 'companion offline — keep exploring' : 'companion offline — still listening'}
          </motion.span>
        ) : null}

        {!companionOffline && usingFallback ? (
          <motion.span
            key="fallback"
            className="hc-chip"
            data-testid="hc-chip-fallback"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={CALM_FADE}
          >
            ideas from theory
          </motion.span>
        ) : null}

        {storageUnavailable ? (
          <motion.span
            key="no-storage"
            className="hc-chip"
            data-testid="hc-chip-no-storage"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={CALM_FADE}
          >
            nothing can be kept on this device
          </motion.span>
        ) : null}

        {paused ? (
          <motion.span
            key="paused"
            className="hc-chip"
            data-testid="hc-chip-paused"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={CALM_FADE}
          >
            paused
          </motion.span>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
